import { ProductSearchResult } from "@/api/product_catalog"
import { exactCurrencyFormatter } from "../../../../utils"
import { Promotion, PromotionLineData } from "../types"
import {
  DealTerms,
  cases_from_units,
  format_money,
  line_spend,
  line_totals,
  line_unit_discount,
  suggested_regular_price,
  units_from_cases,
} from "../deal_math"
import { NumberTextInput, field_input_class } from "./form_fields"

/** Catalog details for the products on the form, by product id. */
export type ProductDetails = Record<string, ProductSearchResult>

interface PromotionLinesEditorProps {
  promotion: Promotion
  onChange: (promotion: Promotion) => void
  /** Catalog details for the picked products (case pack, prices), when loaded. */
  products: ProductDetails
}

/** PURE: the key a line is matched to a product chip on — the catalog id, else the label. */
export function line_key(line: Pick<PromotionLineData, "product_id" | "product_name">): string {
  return line.product_id || `label:${line.product_name ?? ""}`
}

/**
 * PURE: one line per product on the form, in chip order. Lines already entered keep their values
 * (matched by product id, else by name); products with no line yet get one carrying the header's
 * terms, with the regular price suggested from the catalog; lines whose product was removed go.
 */
export function lines_for_products(
  promotion: Pick<Promotion, "products" | "product_ids" | "lines" | "regular_price" | "customer" | "retailer">,
  products: ProductDetails,
  today: string,
): PromotionLineData[] {
  const existing = new Map((promotion.lines ?? []).map((line) => [line_key(line), line]))
  const ids = promotion.product_ids ?? []
  const labels = promotion.products ?? []
  const count = Math.max(ids.length, labels.length)
  const next: PromotionLineData[] = []
  for (let index = 0; index < count; index += 1) {
    const product_id = ids[index]
    const product_name = labels[index] || products[product_id]?.name || product_id
    if (!product_id && !product_name) continue
    const key = product_id || `label:${product_name}`
    const kept = existing.get(key)
    if (kept) {
      next.push({ ...kept, product_name: kept.product_name || product_name })
      continue
    }
    const details = product_id ? products[product_id] : undefined
    const suggested = details ? suggested_regular_price(details, promotion.customer, promotion.retailer, today) : null
    next.push({
      product_id: product_id || undefined,
      product_name,
      upc: details?.upc ?? undefined,
      regular_shopper_price: suggested?.amount ?? promotion.regular_price,
    })
  }
  return next
}

/** PURE: a line with its units changed, cases following through the case pack (and the reverse). */
export function line_with_units(line: PromotionLineData, units: number | undefined, units_per_case: number | undefined): PromotionLineData {
  return { ...line, planned_volume_units: units, planned_volume_cases: cases_from_units(units, units_per_case) ?? line.planned_volume_cases }
}

export function line_with_cases(line: PromotionLineData, cases: number | undefined, units_per_case: number | undefined): PromotionLineData {
  return { ...line, planned_volume_cases: cases, planned_volume_units: units_from_cases(cases, units_per_case) ?? line.planned_volume_units }
}

function today_iso(): string {
  return new Date().toISOString().slice(0, 10)
}

const CELL = field_input_class({}) + " px-2 py-1 text-sm"
const MONEY_CELL = field_input_class({ prefix: "$" }) + " py-1 text-sm"

/**
 * Per-product deal terms and volume. Each product the promotion covers gets a row with its own
 * regular price, discount and volume, so a promo over several items at different prices can be
 * modelled instead of one discount applying to all of them (Bakhrom, 1st Phorm). Blank cells fall
 * back to the header's terms; the header's volume and estimate become the sum of the rows.
 */
export function PromotionLinesEditor({ promotion, onChange, products }: PromotionLinesEditorProps) {
  const header: DealTerms = promotion
  const lines = lines_for_products(promotion, products, today_iso())
  const totals = line_totals(lines, header)

  function update_line(index: number, next: PromotionLineData) {
    const updated = lines.map((line, position) => (position === index ? next : line))
    onChange({ ...promotion, lines: updated })
  }

  if (lines.length === 0) {
    return <p class="text-xs text-gray-500">Pick products above and each one gets its own row here.</p>
  }

  return (
    <div class="space-y-2">
      <div class="overflow-x-auto rounded-md border border-gray-200">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
            <tr>
              <th class="px-2 py-2">Product</th>
              <th class="px-2 py-2">Regular price</th>
              <th class="px-2 py-2">Off per unit</th>
              <th class="px-2 py-2">Off %</th>
              <th class="px-2 py-2">Units</th>
              <th class="px-2 py-2">Cases</th>
              <th class="px-2 py-2 text-right">Spend</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            {lines.map((line, index) => {
              const details = line.product_id ? products[line.product_id] : undefined
              const pack = details?.units_per_case
              const unit_cost = line_unit_discount(line, header)
              const spend = line_spend(line, header)
              return (
                <tr key={line_key(line)} class="align-top">
                  <td class="px-2 py-1.5">
                    <div class="font-medium text-gray-900">{line.product_name}</div>
                    <div class="text-xs text-gray-500">{pack ? `${pack} per case` : "case pack unknown"}</div>
                  </td>
                  <td class="px-2 py-1.5">
                    <div class="relative w-28">
                      <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-xs text-gray-500">$</span>
                      <NumberTextInput value={line.regular_shopper_price} onChange={(value) => update_line(index, { ...line, regular_shopper_price: value })} class={MONEY_CELL} money placeholder="0.00" />
                    </div>
                  </td>
                  <td class="px-2 py-1.5">
                    <div class="relative w-24">
                      <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-xs text-gray-500">$</span>
                      <NumberTextInput value={line.discount_amount} onChange={(value) => update_line(index, { ...line, discount_amount: value })} class={MONEY_CELL} money placeholder={unit_cost != null && !line.discount_amount ? unit_cost.toFixed(2) : "header"} />
                    </div>
                  </td>
                  <td class="px-2 py-1.5">
                    <NumberTextInput value={line.discount_percentage} onChange={(value) => update_line(index, { ...line, discount_percentage: value })} class={`${CELL} w-20`} placeholder="—" />
                  </td>
                  <td class="px-2 py-1.5">
                    <NumberTextInput value={line.planned_volume_units} onChange={(value) => update_line(index, line_with_units(line, value, pack))} class={`${CELL} w-28`} placeholder="0" />
                  </td>
                  <td class="px-2 py-1.5">
                    <NumberTextInput value={line.planned_volume_cases} onChange={(value) => update_line(index, line_with_cases(line, value, pack))} class={`${CELL} w-24`} placeholder="0" />
                  </td>
                  <td class="px-2 py-1.5 text-right tabular-nums">
                    {spend != null ? exactCurrencyFormatter(spend) : <span class="text-gray-400">—</span>}
                    {unit_cost != null && <div class="text-xs text-gray-500">{format_money(unit_cost)}/unit</div>}
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot class="bg-gray-50 text-sm font-medium text-gray-800">
            <tr>
              <td class="px-2 py-2" colSpan={4}>
                Total
              </td>
              <td class="px-2 py-2 tabular-nums">{Math.round(totals.units).toLocaleString("en-US")}</td>
              <td class="px-2 py-2 tabular-nums">{totals.cases.toLocaleString("en-US")}</td>
              <td class="px-2 py-2 text-right tabular-nums">{exactCurrencyFormatter(totals.spend)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p class="text-xs text-gray-500">
        A blank discount uses the header&apos;s terms; typing units fills cases from the product&apos;s case pack and back.
        {totals.lines_without_spend > 0 && ` ${totals.lines_without_spend} row${totals.lines_without_spend === 1 ? " has" : "s have"} units but no price or discount yet, so the total is short by them.`}
      </p>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest
  const bars: ProductSearchResult = {
    id: "p1",
    name: "Level-1 Bar",
    is_pack: false,
    is_sellable: true,
    units_per_case: 12,
    product_groups: [],
    identifiers: [],
    upc: "0001",
    aliases: [],
    prices: [{ customer_name: "KeHE", unit_price: 2.25, currency: "USD" }],
  }

  describe("lines_for_products", () => {
    it("gives every product a row, suggesting the customer's price, and keeps what was typed", () => {
      const lines = lines_for_products(
        { products: ["Level-1 Bar", "Sticks"], product_ids: ["p1"], customer: "KeHE", regular_price: 3, lines: [] },
        { p1: bars },
        "2026-10-16",
      )
      expect(lines).toEqual([
        { product_id: "p1", product_name: "Level-1 Bar", upc: "0001", regular_shopper_price: 2.25 },
        { product_id: undefined, product_name: "Sticks", upc: undefined, regular_shopper_price: 3 },
      ])
      const kept = lines_for_products(
        { products: ["Level-1 Bar"], product_ids: ["p1"], lines: [{ product_id: "p1", discount_amount: 0.5, planned_volume_units: 100 }] },
        { p1: bars },
        "2026-10-16",
      )
      expect(kept[0]).toMatchObject({ product_id: "p1", product_name: "Level-1 Bar", discount_amount: 0.5, planned_volume_units: 100 })
    })

    it("drops the row of a product that was removed", () => {
      const lines = lines_for_products({ products: [], product_ids: [], lines: [{ product_id: "p1", discount_amount: 0.5 }] }, { p1: bars }, "2026-10-16")
      expect(lines).toEqual([])
    })
  })

  describe("units and cases on a line", () => {
    it("follow each other through the case pack", () => {
      expect(line_with_units({}, 1200, 12)).toEqual({ planned_volume_units: 1200, planned_volume_cases: 100 })
      expect(line_with_cases({}, 50, 12)).toEqual({ planned_volume_cases: 50, planned_volume_units: 600 })
      // No case pack: the other side is left as it was.
      expect(line_with_units({ planned_volume_cases: 7 }, 100, undefined)).toEqual({ planned_volume_units: 100, planned_volume_cases: 7 })
    })
  })
}
